import axios from "axios";
import { signOut } from "firebase/auth";
import auth from "./fire";
import * as Sentry from "@sentry/react";

let setGlobalError: ((msg: string) => void) | null = null;

export const setAxiosGlobalErrorHandler = (fn: (msg: string) => void) => {
  setGlobalError = fn;
};
const axiosClient = (() => {
  const apiURL = process.env.REACT_APP_API_URL;

  const getAuthToken = async () => {
    try {
      return "Bearer " + (await auth.currentUser?.getIdToken());
    } catch (err) {
      console.log("getAuthToken: ", err);
    }
  };

  const instance = axios.create({
    baseURL: apiURL,
  });

  const getAuthUserId = async () => {
    try {
      return await auth.currentUser?.uid;
    } catch (err) {
      console.log("getAuthUserId: ", err);
    }
  };

  instance.interceptors.request.use(async (config) => {
    config.headers.Authorization = await getAuthToken();
    config.params = config.params || {};
    config.params["userId"] = await getAuthUserId();
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const environment = process.env.REACT_APP_ENVIRONMENT_LEVEL;

      if (environment === "development") {
        console.error("AXIOS ERROR: ", error);
      }

      const status = error.response?.status;
      const requestUrl = error.config?.url || "";

      // The invite check is a pre-auth probe where a 4xx ("not invited" / "used")
      // is a normal answer, not a bug — don't report it or pop the error overlay.
      if (requestUrl.includes("/api/users/check-invite")) {
        return Promise.reject(error);
      }

      // 401 = the session token is missing/expired/revoked. This is an expected
      // auth condition (not a bug): skip Sentry/overlay, clear the stale Firebase
      // session (otherwise the login page would bounce the still-"authenticated"
      // user straight back and loop), then redirect to login — unless the user is
      // already on a public page.
      if (status === 401) {
        const path = window.location.pathname;
        const onPublicPage =
          path.startsWith("/login") ||
          path.startsWith("/sign-up") ||
          path.startsWith("/forgot-password") ||
          path.startsWith("/reset-password") ||
          path.startsWith("/auth/action");
        if (!onPublicPage) {
          // Redirect once sign-out settles (success OR failure) so the reloaded
          // login page doesn't see a stale persisted session. Using .then(cb, cb)
          // instead of .finally avoids depending on lib >= es2018.
          const goToLogin = () => window.location.assign("/login");
          signOut(auth).then(goToLogin, goToLogin);
        }
        return Promise.reject(error);
      }

      if (axios.isAxiosError(error)) {
        Sentry.withScope((scope) => {
          scope.setTag("error_type", "axios");
          scope.setExtra("status", error.response?.status);
          scope.setExtra("url", error.config?.url);
          scope.setExtra("method", error.config?.method);
          scope.setExtra("params", error.config?.params);
          scope.setExtra("headers", error.config?.headers);
          scope.setExtra("response_data", error.response?.data || null);

          Sentry.captureException(error);
        });
      } else {
        // Fallback for non-Axios errors
        Sentry.captureException(error);
      }

      const message =
        error.response?.data?.message || "An unexpected error occurred.";

      // Only trigger the full page error for 5xx server errors or network errors.
      // 4xx client errors should generally be caught and handled gracefully by the UI.
      const isServerError = !error.response || error.response.status >= 500;

      // Call the global error handler
      if (setGlobalError && isServerError) {
        setGlobalError(message);
      }

      return Promise.reject(error);
    },
  );

  return instance;
})();

export default axiosClient;
